/**
 * Unterscheidung von Tippen und Ziehen.
 *
 * Der Joystick liegt ueber der halben Spielflaeche. Ohne diese Unterscheidung
 * wuerde jedes Antippen eines Gegners auf der linken Seite nur den Joystick
 * aufrufen - die Zielauswahl kaeme nie an.
 *
 * Die Regel steht bewusst hier und nicht mitten in der Oberflaeche: So laesst
 * sie sich ohne Browser pruefen und spaeter an einer Stelle nachjustieren.
 */

/**
 * Bis zu dieser Dauer gilt eine Beruehrung als Tippen.
 * Grosszuegig gewaehlt - ein Daumen ist langsamer als eine Maus.
 */
export const TAP_MAX_DURATION_MS = 250;

/**
 * Bis zu dieser groessten Auslenkung gilt eine Beruehrung als Tippen.
 * Ebenfalls grosszuegig: Ein versehentlicher Schritt ist harmloser als eine
 * Zielauswahl, die nicht ankommt.
 */
export const TAP_MAX_DISTANCE = 14;

/**
 * War eine Beruehrung ein Tippen?
 *
 * @param durationMs  Dauer der Beruehrung in Millisekunden.
 * @param maxDistance Die GROESSTE Auslenkung waehrend der Beruehrung - nicht
 *   die letzte. Wer weit zieht und wieder zurueckkommt, wollte laufen.
 */
export function isTap(durationMs: number, maxDistance: number): boolean {
  if (!Number.isFinite(durationMs) || !Number.isFinite(maxDistance)) return false;
  return durationMs <= TAP_MAX_DURATION_MS && maxDistance <= TAP_MAX_DISTANCE;
}

/**
 * Ab dieser Dauer gilt eine Beruehrung als langes Druecken (Masterprompt
 * Abschnitt 74: Last Wave - kurz tippen sendet erneut, lange druecken
 * bearbeitet). Lang genug, dass ein normales Tippen nie versehentlich
 * darunter faellt, kurz genug, dass niemand ungeduldig wird.
 */
export const LONG_PRESS_MS = 450;

/**
 * Wie oft ein gehaltener Knopf wiederholt ausloest (Abschnitt 70: halten =
 * mehrfach hinzufuegen). Die erste Wiederholung kommt erst nach einer kleinen
 * Pause - sonst wuerde schon ein normales Tippen doppelt zaehlen.
 */
export const HOLD_REPEAT_DELAY_MS = 350;
export const HOLD_REPEAT_INTERVAL_MS = 110;

/** Wie viele Wiederholungen ein Halten dieser Dauer ausgeloest hat (ohne den ersten Tipp). */
export function countHoldRepeats(heldMs: number): number {
  if (!Number.isFinite(heldMs) || heldMs < HOLD_REPEAT_DELAY_MS) return 0;
  return Math.floor((heldMs - HOLD_REPEAT_DELAY_MS) / HOLD_REPEAT_INTERVAL_MS) + 1;
}

/** Tippen, langes Druecken - oder keins von beiden, weil der Finger gewandert ist. */
export type PressKind = 'tap' | 'long' | 'none';

export function classifyPress(durationMs: number, maxDistance: number): PressKind {
  if (!Number.isFinite(durationMs) || !Number.isFinite(maxDistance)) return 'none';
  if (maxDistance > TAP_MAX_DISTANCE) return 'none';
  return durationMs >= LONG_PRESS_MS ? 'long' : 'tap';
}

/**
 * So lange nach einem Tippen gilt ein click-Ereignis als Nachhall desselben
 * Tippens (im Spiel aufgefallen am 27.09.2026).
 *
 * Hintergrund: Auf dem Handy erzeugt der Browser nur fuer den ERSTEN Finger
 * ein click-Ereignis. Wer mit dem linken Daumen laeuft und mit dem rechten
 * einen Knopf antippt, loest deshalb gar kein click aus - der Knopf tut
 * nichts. Deshalb reagieren die Knoepfe im HUD auf pointerup.
 *
 * Beim normalen Tippen mit einem Finger kommt das click danach trotzdem noch.
 * Ohne diese Sperre wuerde der Knopf dann zweimal ausloesen.
 *
 * Reichlich bemessen: Der Browser schickt das click ueblicherweise binnen
 * weniger Millisekunden, in Ausnahmefaellen (300ms-Verzoegerung alter
 * Browser) aber deutlich spaeter.
 */
export const CLICK_ECHO_MS = 700;

/**
 * Ist dieses click nur der Nachhall eines Tippens, das schon gezaehlt wurde?
 *
 * @param msSeitTipp Millisekunden seit dem letzten per pointerup gezaehlten
 *   Tippen. Unendlich, wenn es noch keines gab - dann ist es ein echtes click,
 *   zum Beispiel von der Tastatur.
 */
export function isClickEcho(msSeitTipp: number): boolean {
  if (!Number.isFinite(msSeitTipp)) return false;
  return msSeitTipp >= 0 && msSeitTipp < CLICK_ECHO_MS;
}
